import { createTheme } from '@mui/material'

export const lightTheme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 1000,
      lg: 1200,
      xl: 1920,
    },
  },
  palette: {
    mode: 'light',
    neutral: {
      100: '#F3F4F6',
      200: '#E5E7EB',
      300: '#D1D5DB',
      400: '#9CA3AF',
      500: '#6B7280',
      600: '#4B5563',
      700: '#545454',
      800: '#1F2937',
      900: '#1D6AC6',
      950: '#fff',
    },
    action: {
      active: '#6B7280',
      focus: 'rgba(55, 65, 81, 0.12)',
      hover: 'rgba(55, 65, 81, 0.04)',
      selected: 'rgba(55, 65, 81, 0.08)',
      disabledBackground: 'rgba(55, 65, 81, 0.12)',
      disabled: '#37415142' //'rgba(55, 65, 81, 0.26)',
    },
    background: {
      default: '#fff',
      paper: '#FFFFFF',
      secondary: '#E4EEFB',
      primaryLightest: '#F2F8FF',
    },
    divider: '#D0D0CE',
    primary: {
      main: '#095BBD',
      // light: '#095BBD',
      dark: '#000',
      darker: '#063D7E',
      contrastText: '#fff',
    },
    secondary: {
      main: '#686867',
      // light: '#EFEFEF',
      dark: '#000000',
      darker: '#686867',
      contrastText: '#fff',
    },
    success: {
      main: '#21B979',
      // light: '#43C6B7',
      dark: '#0E8074',
      darker: '#0E8074',
      contrastText: '#FFFFFF',
    },
    info: {
      main: '#2196F3',
      // light: '#64B6F7',
      dark: '#0B79D0',
      darker: '#0B79D0',
      contrastText: '#FFFFFF',
    },
    warning: {
      main: '#FFB020',
      // light: '#FFBF4C',
      dark: '#B27B16',
      darker: '#B27B16',
      contrastText: '#FFFFFF',
    },
    error: {
      main: '#D14343',
      // light: '#DA6868',
      dark: '#922E2E',
      darker: '#922E2E',
      contrastText: '#FFFFFF',
    },
    gray: {
      main: '#686867',
      // light: '#EFEFEF',
      dark: '#000000',
      darker: '#686867',
      contrastText: '#fff',
    },
    dark: {
      main: '#000',
      // light: '#000',
      dark: '#095BBD',
      darker: '#063D7E',
      contrastText: '#fff',
    },
    light: {
      main: '#454545',
      // light: '#D8D8D6',
      dark: '#095BBD',
      darker: '#454545',
      contrastText: '#454545',
    },
    text: {
      primary: '#000',
      secondary: '#454545',
      disabled: 'rgba(55, 65, 81, 0.48)',
      primaryTemp: '#000',
      // contrastText: '#000'
    },
    input: {
      background: '#FFFFFF',
      text: '#000000',
      placeholder: '#8B8B89',
      border: '#000000',
    },
  },
  components: {
    MuiButton: {
      defaultProps: {
        disableElevation: true,
        size: 'medium',
      },
      styleOverrides: {
        sizeSmall: {
          height: '28px',
          minWidth: '28px',
          padding: '0 12px',
        },
        sizeMedium: {
          height: '35px',
          minWidth: '35px',
          padding: '0 16px',
        },
        sizeLarge: {
          height: '44px',
          minWidth: '44px',
          padding: '0 20px',
        },
        root: ({ theme, ownerState }) => {
          return {
            textTransform: 'none',
            borderRadius: '4px',
            fontWeight: 500,
            fontSize: '14px',
            minWidth: 'auto',
          }
        },
        contained: ({ theme, ownerState }) => {
          if (ownerState.color === 'light') {
            return {
              backgroundColor: '#fff',
              color: theme.palette.light.main,
              outline: `1px solid ${theme.palette.light.main}4D`,
              boxShadow: 'none',

              '&:hover': {
                backgroundColor: '#fff',
                outline: `1px solid ${theme.palette.light.dark}`,
                color: theme.palette.light.dark,
              },
              '&.active': {
                backgroundColor: '#fff',
                outline: `1px solid ${theme.palette.light.darker}`,
                color: theme.palette.light.darker,
              },
              '&:active, &.active': {
                backgroundColor: '#fff',
                outline: `1px solid ${theme.palette.light.darker}`,
                color: theme.palette.light.darker,
              },
              '&.Mui-disabled': {
                backgroundColor: theme.palette.action.disabledBackground,
                color: theme.palette.action.disabled,
                outline: `0px solid ${theme.palette.action.disabled}`,
              },
              '&.Mui-disabled svg path': {
                fill: theme.palette.action.disabled,
              },
            }
          }
          return {
            '&.MuiButton-contained:active, &.MuiButton-contained.active, &.MuiButton-contained.active:hover':
              {
                backgroundColor: theme.palette[ownerState?.color || {}]?.darker || '#fff',
              },
            '&.Mui-disabled': {
              backgroundColor: theme.palette.action.disabledBackground,
              color: theme.palette.action.disabled,
              outline: `0px solid ${theme.palette.action.disabled}`,
            },
            '&.Mui-disabled svg path': {
              fill: theme.palette.action.disabled,
            },
          }
        },
        outlined: ({ theme, ownerState }) => {
          return {
            border: ownerState.color === 'primary' ? `1px solid ${lightTheme.palette.primary.main}` : undefined,
            backgroundColor: 'transparent',
            '&:hover': {
              borderColor: theme.palette[ownerState?.color || {}]?.dark || '#fff',
              color: theme.palette[ownerState?.color || {}]?.dark || '#fff',
            },
            '&.MuiButton-outlined:hover .MuiButton-startIcon svg path, &.MuiButton-outlined:hover .MuiButton-endIcon svg path':
              {
                fill: theme.palette[ownerState?.color || {}]?.dark || '#fff',
              },
            '&.MuiButton-outlined:active .MuiButton-startIcon svg path, &.MuiButton-outlined:active .MuiButton-endIcon svg path':
              {
                fill: theme.palette[ownerState?.color || {}]?.darker || '#fff',
              },
            '&.MuiButton-outlined.active .MuiButton-startIcon svg path, &.MuiButton-outlined.active .MuiButton-endIcon svg path, &.MuiButton-outlined.active:hover .MuiButton-startIcon svg path, &.MuiButton-outlined.active:hover .MuiButton-endIcon svg path':
              {
                fill: '#fff',
              },
            '&:active, &.active, &.active:hover': {
              borderColor: theme.palette[ownerState?.color || {}]?.darker || '#fff',
              color: theme.palette[ownerState?.color || {}]?.darker || '#fff',
            },
          }
        },
        text: ({ theme, ownerState }) => {
          return {
            '&.MuiButton-text .MuiButton-startIcon svg path, &.MuiButton-text .MuiButton-endIcon svg path':{
              fill: theme.palette[ownerState?.color || {}]?.main || '#fff',
            },
            '&.MuiButton-text:hover .MuiButton-startIcon svg path, &.MuiButton-text:hover .MuiButton-endIcon svg path':{
              fill: theme.palette[ownerState?.color || {}]?.dark || '#fff',
            },
            '&.MuiButton-text:active .MuiButton-startIcon svg path, &.MuiButton-text:active .MuiButton-endIcon svg path':{
              fill: theme.palette[ownerState?.color || {}]?.darker || '#fff',
            },
            '&.MuiButton-text.active .MuiButton-startIcon svg path, &.MuiButton-text.active .MuiButton-endIcon svg path, &.MuiButton-text.active:hover .MuiButton-startIcon svg path, &.MuiButton-outlined.active:hover .MuiButton-endIcon svg path':{
              fill: theme.palette[ownerState?.color || {}]?.darker || '#fff',
            },
            '&.Mui-disabled svg path': {
              fill: theme.palette.action.disabled + ' !important',
            },
          }
        },
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '32px 24px',
          '&:last-child': {
            paddingBottom: '32px',
          },
        },
      },
    },
    MuiCardHeader: {
      defaultProps: {
        titleTypographyProps: {
          variant: 'h6',
        },
        subheaderTypographyProps: {
          variant: 'body2',
        },
      },
      styleOverrides: {
        root: {
          padding: '32px 24px',
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        '*': {
          boxSizing: 'border-box',
          margin: 0,
          padding: 0,
        },
        html: {
          MozOsxFontSmoothing: 'grayscale',
          WebkitFontSmoothing: 'antialiased',
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100%',
          width: '100%',
        },
        body: {
          display: 'flex',
          flex: '1 1 auto',
          flexDirection: 'column',
          minHeight: '100%',
          width: '100%',
          '&>iframe': {
            display: 'none',
          },
        },
        '#__next': {
          display: 'flex',
          flex: '1 1 auto',
          flexDirection: 'column',
          height: '100%',
          width: '100%',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          borderColor: '#E6E8F0',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: '#F3F4F6',
          '.MuiTableCell-root': {
            color: '#000',
          },
          borderBottom: 'none',
          '& .MuiTableCell-root': {
            borderBottom: 'none',
            fontSize: '12px',
            fontWeight: 600,
            lineHeight: 1,
            letterSpacing: 0.5,
            textTransform: 'uppercase',
          },
          '& .MuiTableCell-paddingCheckbox': {
            paddingTop: 4,
            paddingBottom: 4,
          },
        },
      },
    },
  },
  shape: {
    borderRadius: 0,
    round: {
      borderRadius: '10px',
    },
  },
  shadows: [
    'none',
    '0px 1px 1px rgba(100, 116, 139, 0.06), 0px 1px 2px rgba(100, 116, 139, 0.1)',
    '0px 1px 2px rgba(100, 116, 139, 0.12)',
    '0px 1px 4px rgba(100, 116, 139, 0.12)',
    '0px 1px 5px rgba(100, 116, 139, 0.12)',
    '0px 1px 6px rgba(100, 116, 139, 0.12)',
    '0px 2px 6px rgba(100, 116, 139, 0.12)',
    '0px 3px 6px rgba(100, 116, 139, 0.12)',
    '0px 2px 4px rgba(31, 41, 55, 0.06), 0px 4px 6px rgba(100, 116, 139, 0.12)',
    '0px 5px 12px rgba(100, 116, 139, 0.12)',
    '0px 5px 14px rgba(100, 116, 139, 0.12)',
    '0px 5px 15px rgba(100, 116, 139, 0.12)',
    '0px 6px 15px rgba(100, 116, 139, 0.12)',
    '0px 7px 15px rgba(100, 116, 139, 0.12)',
    '0px 8px 15px rgba(100, 116, 139, 0.12)',
    '0px 9px 15px rgba(100, 116, 139, 0.12)',
    '0px 10px 15px rgba(100, 116, 139, 0.12)',
    '0px 12px 22px -8px rgba(100, 116, 139, 0.25)',
    '0px 13px 22px -8px rgba(100, 116, 139, 0.25)',
    '0px 14px 24px -8px rgba(100, 116, 139, 0.25)',
    '0px 10px 10px rgba(31, 41, 55, 0.04), 0px 20px 25px rgba(31, 41, 55, 0.1)',
    '0px 25px 50px rgba(100, 116, 139, 0.25)',
    '0px 25px 50px rgba(100, 116, 139, 0.25)',
    '0px 25px 50px rgba(100, 116, 139, 0.25)',
    '0px 25px 50px rgba(100, 116, 139, 0.25)',
  ],
  typography: {
    button: {
      fontWeight: 500,
    },
    // fontFamily__: '"Poppins", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji"',
    fontFamily: '"Poppins", Helvetica, Arial, sans-serif',
    body1: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.875rem',
      fontWeight: 400,
      lineHeight: 1.57,
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: 1.75,
    },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 500,
      lineHeight: 1.57,
    },
    overline: {
      fontSize: '0.75rem',
      fontWeight: 600,
      letterSpacing: '0.5px',
      lineHeight: 2.5,
      textTransform: 'uppercase',
    },
    caption: {
      fontSize: '0.75rem',
      fontWeight: 400,
      lineHeight: 1.66,
    },
    h1: {
      fontWeight: 500,
      fontSize: '3.5rem',
      lineHeight: 1.375,
    },
    h2: {
      fontWeight: 500,
      fontSize: '3rem',
      lineHeight: 1.375,
    },
    h3: {
      fontWeight: 500,
      fontSize: '2.25rem',
      lineHeight: 1.375,
    },
    h4: {
      fontWeight: 500,
      fontSize: '2rem',
      lineHeight: 1.375,
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.375,
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.125rem',
      lineHeight: 1.375,
    },
  },

  overrides: {
    MuiButton: {
      sizeLarge: {
        margin: '11px 0',
      },
      palette: {
        neutral: '#6B7280',
      },
    },
  },
})

export const darkTheme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 1000,
      lg: 1200,
      xl: 1920,
    },
  },
  components: {
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 0,
          fontWeight: 500,
          fontSize: '14px',
          lineHeight: '21px',
        },
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '32px 24px',
          '&:last-child': {
            paddingBottom: '32px',
          },
        },
      },
    },
    MuiCardHeader: {
      defaultProps: {
        titleTypographyProps: {
          variant: 'h6',
        },
        subheaderTypographyProps: {
          variant: 'body2',
        },
      },
      styleOverrides: {
        root: {
          padding: '32px 24px',
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        '*': {
          boxSizing: 'border-box',
          margin: 0,
          padding: 0,
        },
        html: {
          MozOsxFontSmoothing: 'grayscale',
          WebkitFontSmoothing: 'antialiased',
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100%',
          width: '100%',
        },
        body: {
          display: 'flex',
          flex: '1 1 auto',
          flexDirection: 'column',
          minHeight: '100%',
          width: '100%',
          '&>iframe': {
            display: 'none',
          },
        },
        '#__next': {
          display: 'flex',
          flex: '1 1 auto',
          flexDirection: 'column',
          height: '100%',
          width: '100%',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          borderColor: '#E6E8F0',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: '#F3F4F6',
          '.MuiTableCell-root': {
            color: '#000',
          },
          borderBottom: 'none',
          '& .MuiTableCell-root': {
            borderBottom: 'none',
            fontSize: '12px',
            fontWeight: 600,
            lineHeight: 1,
            letterSpacing: 0.5,
            textTransform: 'uppercase',
          },
          '& .MuiTableCell-paddingCheckbox': {
            paddingTop: 4,
            paddingBottom: 4,
          },
        },
      },
    },
  },
  palette: {
    mode: 'dark',
    neutral: {
      100: '#F3F4F6',
      200: '#E5E7EB',
      300: '#D1D5DB',
      400: '#9CA3AF',
      500: '#6B7280',
      600: '#4B5563',
      700: '#545454',
      800: '#1F2937',
      // 900: '#111827',
      // 900: '#f7f7f7'
      900: '#1D6AC6',
      950: '#fff',
    },
    action: {
      active: '#6B7280',
      focus: 'rgba(55, 65, 81, 0.12)',
      hover: 'rgba(55, 65, 81, 0.04)',
      selected: 'rgba(55, 65, 81, 0.08)',
      disabledBackground: 'rgba(55, 65, 81, 0.12)',
      disabled: 'rgba(55, 65, 81, 0.26)',
    },
    background: {
      default: '#000',
      paper: '#1D2023',
      secondary: '#000',
      primaryLightest: '#1E2225',
    },
    divider: '#323639',
    primary: {
      main: '#ffffff',
      light: '#1E2225',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#000',
      light: '#95A1AC',
      dark: '#424141',
      contrastText: '#FFFFFF',
    },
    success: {
      main: '#21B979',
      light: '#43C6B7',
      dark: '#0E8074',
      contrastText: '#FFFFFF',
    },
    info: {
      main: '#2196F3',
      light: '#64B6F7',
      dark: '#0B79D0',
      contrastText: '#FFFFFF',
    },
    warning: {
      main: '#FFB020',
      light: '#FFBF4C',
      dark: '#B27B16',
      contrastText: '#FFFFFF',
    },
    error: {
      main: '#D14343',
      light: '#DA6868',
      dark: '#922E2E',
      contrastText: '#FFFFFF',
    },
    gray: {
      main: '#E7E7E7',
      light: '#E7E7E7',
      dark: '#E7E7E7',
      contrastText: '#545454',
    },
    dark: {
      main: '#000',
      light: '#000',
      dark: '#000',
      contrastText: '#fff',
    },
    light: {
      main: '#fff',
      light: '#fff',
      dark: '#fff',
      contrastText: '#000',
    },
    text: {
      primary: '#000',
      secondary: '#95A1AC',
      disabled: 'rgba(55, 65, 81, 0.48)',
      primaryTemp: '#fff',
    },
    input: {
      background: '#454545',
      text: '#FFFFFF',
      placeholder: '#FFFFFF',
      border: '#454545',
    },
  },

  shape: {
    borderRadius: 0,
    round: {
      borderRadius: '10px',
    },
  },
  shadows: [
    'none',
    '0px 1px 1px rgba(100, 116, 139, 0.06), 0px 1px 2px rgba(100, 116, 139, 0.1)',
    '0px 1px 2px rgba(100, 116, 139, 0.12)',
    '0px 1px 4px rgba(100, 116, 139, 0.12)',
    '0px 1px 5px rgba(100, 116, 139, 0.12)',
    '0px 1px 6px rgba(100, 116, 139, 0.12)',
    '0px 2px 6px rgba(100, 116, 139, 0.12)',
    '0px 3px 6px rgba(100, 116, 139, 0.12)',
    '0px 2px 4px rgba(31, 41, 55, 0.06), 0px 4px 6px rgba(100, 116, 139, 0.12)',
    '0px 5px 12px rgba(100, 116, 139, 0.12)',
    '0px 5px 14px rgba(100, 116, 139, 0.12)',
    '0px 5px 15px rgba(100, 116, 139, 0.12)',
    '0px 6px 15px rgba(100, 116, 139, 0.12)',
    '0px 7px 15px rgba(100, 116, 139, 0.12)',
    '0px 8px 15px rgba(100, 116, 139, 0.12)',
    '0px 9px 15px rgba(100, 116, 139, 0.12)',
    '0px 10px 15px rgba(100, 116, 139, 0.12)',
    '0px 12px 22px -8px rgba(100, 116, 139, 0.25)',
    '0px 13px 22px -8px rgba(100, 116, 139, 0.25)',
    '0px 14px 24px -8px rgba(100, 116, 139, 0.25)',
    '0px 10px 10px rgba(31, 41, 55, 0.04), 0px 20px 25px rgba(31, 41, 55, 0.1)',
    '0px 25px 50px rgba(100, 116, 139, 0.25)',
    '0px 25px 50px rgba(100, 116, 139, 0.25)',
    '0px 25px 50px rgba(100, 116, 139, 0.25)',
    '0px 25px 50px rgba(100, 116, 139, 0.25)',
  ],
  typography: {
    button: {
      fontWeight: 500,
    },
    // fontFamily__: '"Poppins", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji"',
    fontFamily: '"Poppins", Helvetica, Arial, sans-serif',
    body1: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.875rem',
      fontWeight: 400,
      lineHeight: 1.57,
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: 1.75,
    },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 500,
      lineHeight: 1.57,
    },
    overline: {
      fontSize: '0.75rem',
      fontWeight: 600,
      letterSpacing: '0.5px',
      lineHeight: 2.5,
      textTransform: 'uppercase',
    },
    caption: {
      fontSize: '0.75rem',
      fontWeight: 400,
      lineHeight: 1.66,
    },
    h1: {
      fontWeight: 500,
      fontSize: '3.5rem',
      lineHeight: 1.375,
    },
    h2: {
      fontWeight: 500,
      fontSize: '3rem',
      lineHeight: 1.375,
    },
    h3: {
      fontWeight: 500,
      fontSize: '2.25rem',
      lineHeight: 1.375,
    },
    h4: {
      fontWeight: 500,
      fontSize: '2rem',
      lineHeight: 1.375,
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.375,
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.125rem',
      lineHeight: 1.375,
    },
  },

  overrides: {
    MuiButton: {
      sizeLarge: {
        margin: '11px 0',
      },
      palette: {
        neutral: '#6B7280',
      },
    },
  },
})
